import { STATUS } from 'customer-data-objects/task/TaskPropertyNames';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useCallback } from 'react';
import getIn from 'transmute/getIn';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import { START_TASK_QUEUE_SCOPES } from '../../constants/scopes';
import { useDispatch, useSelector } from '../../context/Redux';
import { useSearchRequestOptionsForView, useSearchResultsOfView } from '../../hooks/tasksSearchResults';
import { useStartTaskQueue } from '../../hooks/useStartTaskQueue';
import { updateTempViewCrmSearchQuery } from '../../redux/actions/tempViewState';
import { createHasOneOfScopesSelector } from '../../redux/selectors/auth';
import { START_QUEUE_SOURCES, trackSavedViewStartTasksClicked } from '../../tracking/tracker';
import { INCOMPLETE_TASK, isIncompleteTasksFilterApplied } from '../../utils/crmSearchFilters';
import StartTaskQueueUpgrade from './StartTaskQueueUpgrade';
import { jsx as _jsx } from "react/jsx-runtime";
const BUTTON_USE = 'tertiary';
const BUTTON_SIZE = 'sm';
const hasStartQueueScopeSelector = createHasOneOfScopesSelector(START_TASK_QUEUE_SCOPES);
export const StartTaskQueueButton = ({
  viewId
}) => {
  const dispatch = useDispatch();
  const {
    crmSearchQuery
  } = useSearchRequestOptionsForView(viewId);
  const hasStartQueueScope = useSelector(hasStartQueueScopeSelector);
  // Default total to 0 so it is always defined, otherwise translation for button text will fail
  const {
    total = 0,
    loading
  } = useSearchResultsOfView(viewId);
  const startTaskQueue = useStartTaskQueue({
    viewId: String(viewId)
  });
  const handleClick = useCallback(() => {
    startTaskQueue({
      source: START_QUEUE_SOURCES.START_BUTTON
    });
    trackSavedViewStartTasksClicked({
      id: viewId
    });
  }, [startTaskQueue, viewId]);
  const addIncompleteTasksFilter = useCallback(() => {
    const filters = getIn(['filterGroups', 0, 'filters'], crmSearchQuery) || [];
    const filtersWithoutStatus = filters.filter(filter => filter.property !== STATUS);
    const newFilters = [...filtersWithoutStatus, INCOMPLETE_TASK];
    dispatch(updateTempViewCrmSearchQuery({
      viewId,
      crmSearchFilters: newFilters
    }));
  }, [crmSearchQuery, dispatch, viewId]);
  const isButtonDisabled = total <= 0 || loading;
  if (!hasStartQueueScope) {
    return /*#__PURE__*/_jsx(StartTaskQueueUpgrade, {
      total: total
    });
  }
  const filters = getIn(['filterGroups', 0, 'filters'], crmSearchQuery) || [];
  const isOnlyIncompleteTasksShown = isIncompleteTasksFilterApplied(filters);
  if (!isOnlyIncompleteTasksShown) {
    const tooltipTitle = /*#__PURE__*/_jsx(FormattedJSXMessage, {
      elements: {
        Link: UILink
      },
      message: "queues.taskStatusFilterRequiredTooltip_jsx",
      options: {
        onClick: addIncompleteTasksFilter
      }
    });
    return /*#__PURE__*/_jsx(UITooltip, {
      title: tooltipTitle,
      children: /*#__PURE__*/_jsx(UIButton, {
        use: BUTTON_USE,
        onClick: handleClick,
        disabled: true,
        "data-test-id": "start-task-queue-button",
        "data-selenium-test": "start-task-queue-button",
        size: BUTTON_SIZE,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "queues.startNumTasks",
          options: {
            count: total
          }
        })
      })
    });
  }
  const tooltipTitle = /*#__PURE__*/_jsx(FormattedMessage, {
    message: "queues.startQueueDisabledTooltip"
  });
  // only show if disabled due to data not being loaded yet
  const shouldDisableStartQueueDisabledTooltip = total <= 0 || !loading;
  return /*#__PURE__*/_jsx(UITooltip, {
    disabled: shouldDisableStartQueueDisabledTooltip,
    title: tooltipTitle,
    children: /*#__PURE__*/_jsx(UIButton, {
      use: BUTTON_USE,
      onClick: handleClick,
      disabled: isButtonDisabled,
      "data-test-id": "start-task-queue-button",
      "data-selenium-test": "start-task-queue-button",
      size: BUTTON_SIZE,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "queues.startNumTasks",
        options: {
          count: total
        }
      })
    })
  });
};
export default StartTaskQueueButton;